<template>
  <div class="main-content">
    <div class="wrap-content container" id="container">
      <div class="container-fluid h-100 bg-white">
        <div class="row justify-content-center h-100">
          <!-- Chat List (Left Sidebar) -->
          <div class="col-md-4 col-xl-3 chat">
            <div class="card mb-sm-3 mb-md-0 contacts_card">
              <div class="card-header bg-light">
                <div class="input-group m-1">
                  <input
                    v-model="searchQuery"
                    type="text"
                    class="form-control search p-4"
                    placeholder="Search..."
                  />
                </div>
              </div>
              <div class="card-body contacts_body border-top border-bottom">
                <ul class="contacts">
                  <div
                    v-for="chat in sortedAndFilteredChats"
                    :key="chat.id"
                    @click="selectChat(chat)"
                    class="p-3 border-bottom chat-item position-relative"
                    :class="{ active: selectedChat?.id === chat.id }"
                  >
                    <div
                      class="d-flex justify-content-between align-items-start"
                    >
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center">
                          <h6
                            class="mb-1"
                            :class="{ 'fw-bold': chat.unreadCount > 0 }"
                          >
                            <!-- عرض الاسم مع الـ ID -->
                            `<router-link 
                              :to="{name: 'edit-user', params: {id: chat.sender_id}}"
                              class="user-link"
                              @click.stop
                            >
                              <span v-if="chat.username"
                                >#{{ chat.sender_id }} {{ chat.username }}</span
                              >
                              <span v-else>User #{{ chat.sender_id }}</span>
                            </router-link>`
                          </h6>
                          <!-- Unread messages badge -->
                          <div
                            v-if="chat.unreadCount > 0"
                            class="unread-badge ms-2"
                          >
                            {{ chat.unreadCount }}
                          </div>
                        </div>
                        <p
                          class="text-muted small mb-0"
                          :class="{ 'fw-bold': chat.unreadCount > 0 }"
                        >
                          {{ getLastMessagePreview(chat.lastMessage) }}
                        </p>
                      </div>
                      <small class="text-muted">
                        {{ formatTimestamp(chat.lastMessage?.timestamp) }}
                      </small>
                    </div>
                  </div>
                </ul>
              </div>
              <div class="card-footer"></div>
            </div>
          </div>
          <!-- Chat Window (Right) -->
          <div class="col-md-8 col-xl-9 chat">
            <div class="card" v-if="selectedChat">
              <div class="card-header msg_head bg-light">
                <div class="d-flex bd-highlight border-bottom">
                  <div class="img_cont"></div>
                  <div v-if="selectedChat" class="user_info">
                    <!-- عرض الاسم مع الـ ID في الهيدر -->
                    <span v-if="selectedChat.username"
                      >Chat with #{{ selectedChat.sender_id }} {{
                        selectedChat.username
                      }}</span
                    >
                    <span v-else
                      >Chat with User #{{ selectedChat.sender_id }}</span
                    >
                    <p class="mb-1">{{ messages.length }} Messages</p>
                    <span class="badge bg-secondary">
                      {{ supportTypeLabel(selectedChat.support_type) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="card-body msg_card_body bg-light">
                <div v-for="(group, date) in groupedMessages" :key="date">
                  <!-- Date separator -->
                  <div class="text-center my-3">
                    <span class="date-divider">{{
                      formatDateDivider(date)
                    }}</span>
                  </div>
                  <div v-for="message in group" :key="message.id" class="mb-3">
                    <div
                      :class="[
                        'd-flex',
                        message.sender_id === userId
                          ? 'justify-content-end'
                          : 'justify-content-start',
                      ]"
                    >
                      <div
                        :class="[
                          message.sender_id === userId
                            ? 'msg_cotainer_send'
                            : 'msg_cotainer',
                        ]"
                      >
                        <!-- Message text -->
                        <div
                          v-if="message.message && message.message.trim()"
                          class="message-text"
                        >
                          {{ message.message }}
                        </div>

                        <!-- Attachment display -->
                        <div
                          v-if="message.attachment_url || message.attachment"
                          class="attachment-container"
                          :class="{
                            'mt-2': message.message && message.message.trim(),
                          }"
                        >
                          <!-- Image attachment -->
                          <div
                            v-if="
                              isImage(
                                message.attachment_url || message.attachment
                              )
                            "
                            class="image-attachment"
                          >
                            <div class="image-wrapper">
                              <img
                                :src="
                                  message.attachment_url || message.attachment
                                "
                                alt="Attachment"
                                class="attachment-image"
                                @click="
                                  openImageModal(
                                    message.attachment_url || message.attachment
                                  )
                                "
                              />
                              <div class="image-overlay">
                                <button
                                  @click.stop="
                                    downloadFile(
                                      message.attachment_url ||
                                        message.attachment
                                    )
                                  "
                                  class="btn btn-sm btn-light download-overlay-btn"
                                  title="Download image"
                                >
                                  <i class="fas fa-download"></i>
                                </button>
                              </div>
                            </div>
                          </div>

                          <!-- PDF attachment -->
                          <div
                            v-else-if="
                              isPDF(
                                message.attachment_url || message.attachment
                              )
                            "
                            class="pdf-attachment"
                          >
                            <div
                              class="pdf-preview"
                              @click="
                                downloadFile(
                                  message.attachment_url || message.attachment
                                )
                              "
                            >
                              <i class="fas fa-file-pdf pdf-icon"></i>
                              <div class="pdf-info">
                                <span class="pdf-name">{{
                                  getFileName(
                                    message.attachment_url || message.attachment
                                  )
                                }}</span>
                                <small class="pdf-size">PDF Document</small>
                              </div>
                              <div class="download-btn">
                                <i class="fas fa-download"></i>
                              </div>
                            </div>
                          </div>

                          <!-- Other file types -->
                          <div v-else class="file-attachment">
                            <div
                              class="file-preview"
                              @click="
                                downloadFile(
                                  message.attachment_url || message.attachment
                                )
                              "
                            >
                              <i class="fas fa-file file-icon"></i>
                              <div class="file-info">
                                <span class="file-name">{{
                                  getFileName(
                                    message.attachment_url || message.attachment
                                  )
                                }}</span>
                                <small class="file-type">{{
                                  getFileExtension(
                                    message.attachment_url || message.attachment
                                  )
                                }}</small>
                              </div>
                              <div class="download-btn">
                                <i class="fas fa-download"></i>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="message-timestamp small">
                          {{ formatMessageTime(message.timestamp) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <!-- File preview area -->
                <div v-if="selectedFile" class="file-preview-area mb-3">
                  <div class="selected-file-preview">
                    <!-- Image preview -->
                    <div
                      v-if="isImage(selectedFile.name)"
                      class="image-preview"
                    >
                      <img
                        :src="filePreviewUrl"
                        alt="Preview"
                        class="preview-image"
                      />
                    </div>

                    <!-- File info for non-images -->
                    <div v-else class="file-info-preview">
                      <i
                        class="fas"
                        :class="getFileIcon(selectedFile.name)"
                      ></i>
                      <div class="file-details">
                        <span class="file-name">{{ selectedFile.name }}</span>
                        <small class="file-size">{{
                          formatFileSize(selectedFile.size)
                        }}</small>
                      </div>
                    </div>

                    <button
                      @click="removeSelectedFile"
                      class="btn btn-sm btn-danger remove-file-btn"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>

                <div class="chat-input border-top">
                  <!-- File input (hidden) -->
                  <input
                    ref="fileInput"
                    type="file"
                    @change="handleFileSelect"
                    accept="image/*,.pdf,.doc,.docx"
                    style="display: none"
                  />

                  <!-- Attachment button -->
                  <button
                    @click="openFileDialog"
                    class="btn btn-outline-secondary attachment-btn"
                    title="Attach file"
                  >
                    <i class="fas fa-paperclip"></i>
                  </button>

                  <input
                    v-model="newMessage"
                    @keyup.enter="sendMessage"
                    placeholder="Type your message..."
                    class="message-input"
                  />

                  <div class="input-group-append">
                    <button
                      @click="sendMessage"
                      class="btn btn-primary"
                      :disabled="
                        (!newMessage.trim() && !selectedFile) || isUploading
                      "
                    >
                      <span v-if="isUploading">
                        <i class="fas fa-spinner fa-spin"></i>
                      </span>
                      <span v-else>Send</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex-grow-1 d-flex align-items-center justify-content-center"
            >
              <h5 class="text-muted">Select a chat to start messaging</h5>
            </div>
          </div>
        </div>

        <!-- Image Modal -->
        <div
          v-if="imageModal.show"
          class="image-modal-overlay"
          @click="closeImageModal"
        >
          <div class="image-modal-content" @click.stop>
            <button @click="closeImageModal" class="modal-close-btn">
              <i class="fas fa-times"></i>
            </button>
            <img
              :src="imageModal.url"
              alt="Full size image"
              class="modal-image"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import database from "../../firebase";
import { ref, onValue, update, get } from "firebase/database";
import axios from "axios";
import { store } from "../../Store";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import calendar from "dayjs/plugin/calendar";

dayjs.extend(calendar);
dayjs.extend(relativeTime);

export default {
  data() {
    return {
      userId: store.user?.id,
      receiver_id: "",
      searchQuery: "",
      chats: [],
      selectedChat: null,
      messages: [],
      newMessage: "",
      selectedFile: null,
      filePreviewUrl: null,
      isUploading: false,
      imageModal: {
        show: false,
        url: "",
      },
      unsubscribeMessages: null, // إضافة متغير لتخزين دالة إلغاء الاشتراك
    };
  },
  mounted() {
    // Dynamically load Bootstrap CSS
    const bootstrapCSS = document.createElement("link");
    bootstrapCSS.rel = "stylesheet";
    bootstrapCSS.href =
      "https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css";
    bootstrapCSS.integrity =
      "sha384-MCw98/SFnGE8fJT3GXwEOngsV7Zt27NXFoaoApmYm81iuXoPkFOJwJ8ERdknLPMO";
    bootstrapCSS.crossOrigin = "anonymous";
    document.head.appendChild(bootstrapCSS);

    // Load Font Awesome for icons
    const fontAwesome = document.createElement("link");
    fontAwesome.rel = "stylesheet";
    fontAwesome.href =
      "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css";
    document.head.appendChild(fontAwesome);

    console.log("Component mounted, starting to listen for chats...");
    console.log("Mounted - userId:", this.userId);
    console.log("Mounted - store.user:", store.user);
    console.log("Mounted - userPermissions:", store.userPermissions);
    console.log(
      "Mounted - hasPermission chat all-chats:",
      store.hasPermission("chat all-chats")
    );
    console.log(
      "Mounted - hasPermission chat send-message:",
      store.hasPermission("chat send-message")
    );
  },
 computed: {
    sortedAndFilteredChats() {
      // First filter the chats
      let filteredChats = this.chats;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filteredChats = filteredChats.filter(
          (chat) => {
            // Check sender_id
            const senderIdMatch = chat.sender_id && chat.sender_id.toString().toLowerCase().includes(query);
            
            // Check username
            const usernameMatch = chat.username && chat.username.toLowerCase().includes(query);
            
            // Check lastMessage message - with proper null checks
            const messageMatch = chat.lastMessage && 
                                chat.lastMessage.message && 
                                typeof chat.lastMessage.message === 'string' &&
                                chat.lastMessage.message.toLowerCase().includes(query);
            
            return senderIdMatch || usernameMatch || messageMatch;
          }
        );
      }

      // Then sort them by last message timestamp and unread count
      return filteredChats.sort((a, b) => {
        // If one chat has unread messages and the other doesn't, prioritize the unread one
        // if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        // if (b.unreadCount > 0 && a.unreadCount === 0) return 1;

        // If both have or don't have unread messages, sort by timestamp
        const timeA = a.lastMessage?.timestamp
          ? dayjs(a.lastMessage.timestamp).valueOf()
          : 0;
        const timeB = b.lastMessage?.timestamp
          ? dayjs(b.lastMessage.timestamp).valueOf()
          : 0;
        return timeB - timeA;
      });
    },
    sortedMessages() {
      return [...this.messages].sort(
        (a, b) => dayjs(b.timestamp).valueOf() - dayjs(a.timestamp).valueOf() // Reverse order
      );
    },
    groupedMessages() {
      const groups = {};

      this.messages.forEach((message) => {
        const date = dayjs(message.timestamp).format("YYYY-MM-DD");
        if (!groups[date]) {
          groups[date] = [];
        }
        groups[date].push(message);
      });

      // Sort dates in descending order
      const sortedGroups = {};
      Object.keys(groups)
        .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()) // Ascending order
        .forEach((date) => {
          // Sort messages within each group by timestamp (oldest first)
          sortedGroups[date] = groups[date].sort(
            (a, b) =>
              dayjs(a.timestamp).valueOf() - dayjs(b.timestamp).valueOf()
          );
        });
      return sortedGroups;
    },
  },

  created() {
    console.log("Store user:", store.user);
    console.log("Store user id:", store.user?.id);
    console.log("User permissions:", store.userPermissions);
    console.log(
      "Has chat all-chats permission:",
      store.hasPermission("chat all-chats")
    );
    console.log(
      "Has chat send-message permission:",
      store.hasPermission("chat send-message")
    );
    this.listenForChats();
  },

  beforeUnmount() {
    // إلغاء الاشتراك من Firebase عند تدمير المكون
    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
    }
  },

  methods: {
    // File download method
    async downloadFile(url) {
      try {
        const fileName = this.getFileName(url);

        // Create a temporary link element
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        link.target = "_blank";

        // Add to DOM temporarily and trigger click
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Error downloading file:", error);
        // Fallback to opening in new tab
        window.open(url, "_blank");
      }
    },

    // File handling methods
    openFileDialog() {
      this.$refs.fileInput.click();
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          alert("File size must be less than 10MB");
          return;
        }

        this.selectedFile = file;

        // Create preview URL for images
        if (this.isImage(file.name)) {
          this.filePreviewUrl = URL.createObjectURL(file);
        }
      }
    },

    removeSelectedFile() {
      this.selectedFile = null;
      if (this.filePreviewUrl) {
        URL.revokeObjectURL(this.filePreviewUrl);
        this.filePreviewUrl = null;
      }
      this.$refs.fileInput.value = "";
    },

    isImage(fileName) {
      const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
      const extension = fileName.split(".").pop().toLowerCase();
      return imageExtensions.includes(extension);
    },

    isPDF(fileName) {
      return fileName.toLowerCase().endsWith(".pdf");
    },

    getFileName(url) {
      return url.split("/").pop().split("?")[0];
    },

    getFileExtension(fileName) {
      return fileName.split(".").pop().toUpperCase();
    },

    getFileIcon(fileName) {
      const extension = fileName.split(".").pop().toLowerCase();
      switch (extension) {
        case "pdf":
          return "fa-file-pdf";
        case "doc":
        case "docx":
          return "fa-file-word";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
          return "fa-file-image";
        default:
          return "fa-file";
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    getLastMessagePreview(lastMessage) {
      if (!lastMessage) return "No messages";

      const attachmentUrl =
        lastMessage.attachment_url || lastMessage.attachment;

      // إذا كان هناك مرفق
      if (attachmentUrl) {
        // إذا كان هناك نص مع المرفق
        if (lastMessage.message && lastMessage.message.trim()) {
          return lastMessage.message;
        }
        // إذا كان مرفق فقط بدون نص
        else {
          if (this.isImage(attachmentUrl)) {
            return "📷 Image";
          } else if (this.isPDF(attachmentUrl)) {
            return "📄 PDF Document";
          } else {
            return "📎 File";
          }
        }
      }

      // إذا كان نص فقط
      return lastMessage.message || "No messages";
    },

    // Image modal methods
    openImageModal(imageUrl) {
      this.imageModal.show = true;
      this.imageModal.url = imageUrl;
    },

    closeImageModal() {
      this.imageModal.show = false;
      this.imageModal.url = "";
    },

    listenForRealTimeUpdates(chatId) {
      // إلغاء الاشتراك السابق إذا كان موجود
      if (this.unsubscribeMessages) {
        this.unsubscribeMessages();
      }

      const messagesRef = ref(database, `chats/${chatId}/messages`);
      this.unsubscribeMessages = onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const newMessages = Object.entries(data).map(([id, message]) => ({
            id,
            ...message,
          }));

          // استبدال الرسائل بدلاً من دمجها لتجنب التكرار
          this.messages = newMessages;

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      });
    },

    supportTypeLabel(type) {
      switch (type) {
        case "doctor_customer_service":
          return "Doctor Support";
        case "customer_service":
          return "Customer Support";
        default:
          return "Unknown";
      }
    },

    listenForChats() {
      console.log("Starting to listen for chats...");
      const chatsRef = ref(database, "chats");
      onValue(
        chatsRef,
        (snapshot) => {
          console.log("Firebase snapshot received:", snapshot.exists());
          const data = snapshot.val();
          if (data) {
            console.log("Chat data found:", Object.keys(data).length, "chats");
            console.log("Current userId:", this.userId);
            console.log("User permissions:", store.userPermissions);
            console.log(
              "Has chat all-chats permission:",
              store.hasPermission("chat all-chats")
            );
            console.log(
              "Has chat send-message permission:",
              store.hasPermission("chat send-message")
            );

            const chatArray = Object.entries(data).map(([id, chatData]) => {
              const messages = chatData.messages
                ? Object.entries(chatData.messages)
                : [];
              const lastMessage =
                messages.length > 0
                  ? {
                      id: messages[messages.length - 1][0],
                      ...messages[messages.length - 1][1],
                    }
                  : null;

              const unreadCount = messages.reduce((count, messageEntry) => {
                const msg = messageEntry[1]; // Access the message object directly
                const isUnread = msg.sender_id !== this.userId && !msg.seen;
                return count + (isUnread ? 1 : 0);
              }, 0);

              // إيجاد الـ sender_id الصحيح (المستخدم الأساسي وليس الـ admin)
              let actualSenderId = null;
              let username = null; // إضافة متغير للاسم

              if (messages.length > 0) {
                // البحث عن أول رسالة من user (مش admin)
                const userMessage = messages.find(
                  ([, msg]) => msg.sender_id !== this.userId
                );
                if (userMessage) {
                  actualSenderId = userMessage[1].sender_id;
                  username = userMessage[1].username; // جلب الاسم من الرسالة
                } else {
                  // إذا لم نجد رسالة من user، نأخذ من أول رسالة
                  actualSenderId = messages[0][1].sender_id;
                  username = messages[0][1].username; // جلب الاسم من الرسالة
                }
              }

              // إذا لم نجد الاسم في الرسائل، نحاول نجده من بيانات الـ chat نفسها
              if (!username && chatData.username) {
                username = chatData.username;
              }

              // تحديد الـ support_type بناءً على الرسائل أو البيانات المتاحة
              let supportType = chatData.support_type || "customer_service";

              // إذا لم نجد support_type في chatData، نبحث في الرسائل
              if (!chatData.support_type && messages.length > 0) {
                // البحث عن أول رسالة تحتوي على support_type
                const messageWithSupportType = messages.find(
                  ([, msg]) => msg.support_type
                );
                if (messageWithSupportType) {
                  supportType = messageWithSupportType[1].support_type;
                }
              }

              return {
                id,
                sender_id: actualSenderId, // استخدام الـ sender_id الصحيح
                username: username, // إضافة الاسم
                lastMessage,
                unreadCount,
                messages: messages.map(([msgId, msg]) => ({
                  id: msgId,
                  ...msg,
                })),
                support_type: supportType, // استخدام الـ support_type المحدد
              };
            });

            const filteredChats = chatArray.filter((chat) => {
              const hasMessages = chat.messages && chat.messages.length > 0;
              if (!hasMessages) {
                console.log("Chat", chat.id, "has no messages");
                return false;
              }

              // التحقق من صلاحيات المستخدم لعرض المحادثات
              const hasChatPermission =
                store.hasPermission("chat all-chats") ||
                store.hasPermission("chat send-message");

              if (!hasChatPermission) {
                console.log("User doesn't have chat permissions");
                return false;
              }

              console.log("User has chat permissions, showing chat:", chat.id);
              return true;
            });

            console.log("Filtered chats:", filteredChats.length);
            this.chats = filteredChats;

            if (this.selectedChat) {
              const updatedChat = this.chats.find(
                (c) => c.id === this.selectedChat.id
              );
              if (updatedChat) {
                this.selectedChat = updatedChat;
              }
            }
          } else {
            console.log("No chat data found in Firebase");
            this.chats = [];
          }
        },
        (error) => {
          console.error("Error listening for chats:", error);
        }
      );
    },

    selectChat(chat) {
      this.selectedChat = chat;
      this.messages = [];
      // Listen for real-time updates from Firebase
      this.listenForRealTimeUpdates(chat.id);

      // Mark messages as seen
      this.markChatAsSeen(chat.id);
      // Update the unread count for the selected chat
      const updatedChatIndex = this.chats.findIndex((c) => c.id === chat.id);
      if (updatedChatIndex !== -1) {
        this.chats[updatedChatIndex].unreadCount = 0; // Reset unread count
      }
    },

    async markChatAsSeen(chatId) {
      const messagesRef = ref(database, `chats/${chatId}/messages`);
      const snapshot = await get(messagesRef);
      const messages = snapshot.val();

      if (messages) {
        const updates = {};
        Object.entries(messages).forEach(([messageId, message]) => {
          if (message.sender_id !== this.userId && !message.seen) {
            updates[`chats/${chatId}/messages/${messageId}/seen`] = true;
          }
        });

        if (Object.keys(updates).length > 0) {
          await update(ref(database), updates);
        }
      }
    },

    listenForMessages(chatId) {
      const messagesRef = ref(database, `chats/${chatId}/messages`);
      onValue(messagesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          this.messages = Object.entries(data).map(([id, message]) => ({
            id,
            ...message,
          }));
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      });
    },

    async sendMessage() {
      if ((!this.newMessage.trim() && !this.selectedFile) || !this.selectedChat)
        return;

      this.isUploading = true;

      try {
        const formData = new FormData();
        formData.append("message", this.newMessage);
        formData.append("chat_id", this.selectedChat.id);
        formData.append("receiver_id", this.selectedChat.sender_id);

        // Add file if selected
        if (this.selectedFile) {
          formData.append("attachment", this.selectedFile);
        }

        const response = await axios.post(
          "/admin/admin/chat/send-message",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.status === "success") {
          this.newMessage = "";
          this.removeSelectedFile(); // Clear selected file

          // Update the selected chat's support_type if it came from the response
          if (response.data.support_type && this.selectedChat) {
            this.selectedChat.support_type = response.data.support_type;

            // Update the chat in the chats array as well
            const chatIndex = this.chats.findIndex(
              (c) => c.id === this.selectedChat.id
            );
            if (chatIndex !== -1) {
              this.chats[chatIndex].support_type = response.data.support_type;
            }
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        alert("Error sending message. Please try again.");
      } finally {
        this.isUploading = false;
      }
    },

    formatDateDivider(date) {
      const messageDate = dayjs(date);
      const today = dayjs();
      const yesterday = dayjs().subtract(1, "day");

      if (messageDate.isSame(today, "day")) {
        return "Today";
      } else if (messageDate.isSame(yesterday, "day")) {
        return "Yesterday";
      } else if (messageDate.isSame(today, "year")) {
        return messageDate.format("MMMM D"); // e.g., "March 25"
      } else {
        return messageDate.format("MMMM D, YYYY"); // e.g., "March 25, 2023"
      }
    },

    formatMessageTime(timestamp) {
      return dayjs(timestamp).format("hh:mm A"); // This will format time as "04:00 PM"
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return "";
      const date = dayjs(timestamp);
      const today = dayjs();
      const yesterday = dayjs().subtract(1, "day");

      if (date.isSame(today, "day")) {
        return date.format("hh:mm A");
      } else if (date.isSame(yesterday, "day")) {
        return "Yesterday";
      } else if (date.isSame(today, "year")) {
        return date.format("MMM D");
      } else {
        return date.format("MM/DD/YY");
      }
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop =
          this.$refs.messagesContainer.scrollHeight;
      }
    },
  },

  watch: {
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
  },
};
</script>

<style scoped>
.chat-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.chat-item:hover {
  background-color: #007bff14;
}

.chat-item.active {
  background-color: #007bff14;
}

.chat-input {
  display: flex;
  align-items: flex-end;
  padding: 15px 0;
  gap: 10px;
}

.attachment-btn {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
}

.attachment-btn:hover {
  background-color: #e9ecef;
  color: #495057;
}

.message-input {
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.chat-input button {
  padding: 10px 15px;
  background-color: #1e7dc0;
  color: #ffffff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.chat-input button:hover {
  background-color: #1a6ba8;
}

.chat-input button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* File preview area */
.file-preview-area {
  border-top: 1px solid #ddd;
  padding-top: 15px;
}

.selected-file-preview {
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  position: relative;
}

.image-preview {
  margin-right: 10px;
}

.preview-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
}

.file-info-preview {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-info-preview i {
  font-size: 24px;
  margin-right: 10px;
  color: #6c757d;
}

.file-details {
  display: flex;
  flex-direction: column;
}

.file-name {
  font-weight: 500;
  color: #495057;
}

.file-size {
  color: #6c757d;
}

.remove-file-btn {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Message attachments */
.attachment-container {
  max-width: 300px;
}

.image-attachment {
  margin-bottom: 5px;
  position: relative;
}

.image-wrapper {
  position: relative;
  display: inline-block;
}

.image-overlay {
  position: absolute;
  top: 5px;
  right: 5px;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-wrapper:hover .image-overlay {
  opacity: 1;
}

.download-overlay-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: white;
  transform: scale(1.1);
}

.attachment-image {
  width: 100%;
  max-width: 200px;
  min-height: 150px;
  border-radius: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
  object-fit: cover;
  display: block;
}

.attachment-image:hover {
  opacity: 0.9;
}

.pdf-attachment,
.file-attachment {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.2s;
}

.pdf-attachment:hover,
.file-attachment:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
}

.pdf-preview,
.file-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.pdf-icon,
.file-icon {
  font-size: 24px;
  color: #dc3545;
}

.file-icon {
  color: #6c757d;
}

.pdf-info,
.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.pdf-name,
.file-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: inherit;
}

.pdf-size,
.file-type {
  font-size: 0.75rem;
  opacity: 0.8;
}

.download-btn {
  padding: 4px 8px;
  font-size: 0.8rem;
  color: inherit;
  opacity: 0.8;
  transition: opacity 0.2s;
}

.pdf-attachment:hover .download-btn,
.file-attachment:hover .download-btn {
  opacity: 1;
}

.download-btn i {
  font-size: 0.8rem;
}

/* Image Modal */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.image-modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.modal-image {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
}

.modal-close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.modal-close-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.date-divider {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.875rem;
  color: #373737;
}

.unread-badge {
  background-color: #1e7dc0;
  color: white;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  padding: 0 6px;
}

.message-timestamp {
  font-size: 0.7rem;
  opacity: 0.7;
  margin-top: 5px;
  text-align: right;
}

.message-text {
  margin-bottom: 5px;
}

.msg_cotainer {
  padding: 3px 12px;
  padding-top: 15px;
  border-radius: 8px;
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 15px;
  background-color: #ffffff;
  position: relative;
  max-width: 70%;
  word-wrap: break-word;
}

.msg_cotainer_send {
  padding: 3px 12px;
  border-radius: 8px;
  margin-bottom: auto;
  margin-top: auto;
  padding-top: 15px;
  margin-right: 10px;
  background-color: #1e7dc0;
  color: #ffffff;
  position: relative;
  max-width: 70%;
  word-wrap: break-word;
}

.msg_cotainer_send .pdf-attachment,
.msg_cotainer_send .file-attachment {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.msg_cotainer .pdf-attachment,
.msg_cotainer .file-attachment {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
}

.chat {
  margin-top: auto;
  margin-bottom: auto;
}

.card {
  height: 564px;
  border-radius: 15px !important;
  background-color: #ffffff !important;
}

.contacts_body {
  padding: 0.75rem 0 !important;
  overflow-y: auto;
  white-space: nowrap;
}

.msg_card_body {
  overflow-y: auto;
}

.card-header {
  border-radius: 15px 15px 0 0 !important;
  border-bottom: 0px !important;
}

.card-footer {
  border-radius: 0 0 15px 15px !important;
  border-top: 0;
}

.container {
  align-content: center;
}

.search {
  border-radius: 15px !important;
}

.type_msg {
  background-color: #f3f4f6 !important;
  border: 0 !important;
  color: rgb(57, 57, 57) !important;
  height: 60px !important;
  overflow-y: auto;
}

.type_msg:focus {
  box-shadow: none !important;
  outline: 1px !important;
}

.send_btn {
  border-radius: 0 15px 15px 0 !important;
  background-color: #1e7dc0 !important;
  border: 0 !important;
  color: white !important;
  cursor: pointer;
}

.search_btn {
  border-radius: 0 15px 15px 0 !important;
  border: 0 !important;
  cursor: pointer;
}

.contacts {
  list-style: none;
  padding: 0;
}

.contacts li {
  width: 100% !important;
  padding: 5px 10px;
  margin-bottom: 15px !important;
}

.active {
  background-color: #007bff14;
}

.user_info {
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 15px;
  color: #51515114;
  border-bottom: 5px;
}

.user_info span {
  font-size: 20px;
  color: #4b4b4bb9;
}

.user_info p {
  font-size: 10px;
  color: #4b4b4b;
}

.msg_head {
  position: relative;
}

@media (max-width: 576px) {
  .contacts_card {
    margin-bottom: 15px !important;
  }

  .attachment-container {
    max-width: 250px;
  }

  .msg_cotainer,
  .msg_cotainer_send {
    max-width: 85%;
  }
}
</style>
